import { useMemo } from 'react';
import { useCurrentUser } from 'providers/user/UserContext';
import {
  hasFeatureAccess,
  isTierAtLeast,
  getLimitsForTier,
  getTierLabel,
  getMinimumTierForFeature,
  TIER_LABELS,
} from 'utils/featureGating';

/**
 * useFeatureAccess
 *
 * A React hook that reads the current user's subscription tier from UserContext
 * and provides convenient feature-gating helpers.
 *
 * Usage:
 *   const { canAccess, limits, userTier, tierLabel } = useFeatureAccess();
 *
 *   if (canAccess('auto_clip')) { ... }
 */
export default function useFeatureAccess() {
  const { loading, data } = useCurrentUser();

  // Primary: Stripe-managed subscription.tier (set by webhook)
  // Fallback: selectedPlan (set at sign-up)
  // Default: free tier based on user type
  const FREE_TIER_MAP = { athlete: 'athlete_free', employer: 'employer_free', collective: 'collective_free', agency: 'agency_free' };
  const userTier = data?.subscription?.tier || data?.selectedPlan || FREE_TIER_MAP[data?.type] || null;
  const userType = data?.type || null;

  const limits = useMemo(() => getLimitsForTier(userTier), [userTier]);
  const tierLabel = useMemo(() => getTierLabel(userTier), [userTier]);

  /**
   * Check if the current user can access a feature by id.
   * @param {string} featureId – key from FEATURES map
   * @returns {boolean}
   */
  const canAccess = (featureId) => hasFeatureAccess(userTier, featureId);

  /**
   * Check if the user's tier is at least the given tier.
   * @param {string} requiredTier
   * @returns {boolean}
   */
  const isAtLeast = (requiredTier) => isTierAtLeast(userTier, requiredTier);

  /**
   * Get the minimum tier label required to unlock a feature.
   * @param {string} featureId
   * @returns {string|null}
   */
  const getUpgradeTierLabel = (featureId) => {
    const minTier = getMinimumTierForFeature(featureId, userType);
    return minTier ? TIER_LABELS[minTier] : null;
  };

  return {
    loading,
    userTier,
    userType,
    tierLabel,
    limits,
    canAccess,
    isAtLeast,
    getUpgradeTierLabel,
  };
}
